// src/pages/CustomerForm.jsx

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../axios';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent } from '../../components/ui/card';

export default function CustomerForm({ onSuccess, onCancel, isModal = false }) {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const editingId = id ? parseInt(id, 10) : null;

  // ✅ جلب الشركة والفرع من localStorage (محفوظة عند تسجيل الدخول)
  const companyId = parseInt(localStorage.getItem('companyId'), 10) || 0;
  const branchId  = parseInt(localStorage.getItem('branchId'), 10)  || 0;

  const [form, setForm] = useState({
    name:     '',
    taxNumber:'',
    mobile:   '',
    address:  '',
    country:  '',
    city:     '',
    phone:    '',
    email:    '',
    notes:    '',
    isActive: true
  });

  // عند التعديل: جلب بيانات العميل
  useEffect(() => {
    if (!editingId) return;
    axios.get(`/Customer/${editingId}`)
      .then(res => {
        const d = res.data;
        setForm({
          name:     d.name       || '',
          taxNumber:d.taxNumber || '',
          mobile:   d.mobile    || '',
          address:  d.address   || '',
          country:  d.country   || '',
          city:     d.city      || '',
          phone:    d.phone     || '',
          email:    d.email     || '',
          notes:    d.notes     || '',
          isActive: d.isActive
        });
      })
      .catch(() => alert('❌ تعذّر تحميل بيانات العميل'));
  }, [editingId]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // ✅ نضيف companyId و branchId مع البيانات
    const payload = { 
      ...form,
      companyId,
      branchId
    };

    try {
      let response;
      if (editingId) {
        response = await axios.put(`/Customer/${editingId}`, payload);
        alert('✅ تم تحديث العميل بنجاح');
      } else {
        response = await axios.post(`/Customer`, payload);
        alert('✅ تم إضافة العميل بنجاح');
      }
      
      if (onSuccess) {
        // دمج البيانات المدخلة مع البيانات الراجعة من السيرفر (مثل المعرف والكود)
        const newId = response.data.id || response.data.Id;
        const completeCustomer = { 
            ...form, 
            id: newId, 
            code: response.data.code || response.data.Code,
            ...response.data 
        };
        onSuccess(completeCustomer);
      } else {
        navigate('/customers');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors) {
        const msgs = Object.entries(data.errors)
          .map(([f, arr]) => `${f}: ${arr.join(', ')}`)
          .join('\n');
        alert(msgs);
      } else {
        alert(data?.title || '❌ حدث خطأ غير متوقع');
      }
    }
  };

  const Container = isModal ? 'div' : Card;
  const containerProps = isModal ? { className: "w-full" } : { className: "max-w-md mx-auto mt-10 p-6", dir: "rtl" };
  const Content = isModal ? 'div' : CardContent;

  return (
    <Container {...containerProps}>
      <Content>
        {!isModal && (
          <h2 className="text-xl font-bold mb-4">
            {editingId ? 'تعديل عميل' : 'إضافة عميل'}
          </h2>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* نوع العميل */}
          <div className="flex items-center gap-4 mb-4 bg-gray-50 p-3 rounded border">
            <label className="font-bold">نوع العميل:</label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio" 
                name="customerType" 
                value={1} 
                checked={parseInt(form.customerType) === 1} 
                onChange={handleChange}
              />
              فردي
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio" 
                name="customerType" 
                value={2} 
                checked={parseInt(form.customerType) === 2} 
                onChange={handleChange}
              />
              تجاري
            </label>
          </div>

          <Input
            name="name" placeholder="الاسم"
            value={form.name} onChange={handleChange} required
          />
          
          {parseInt(form.customerType) === 2 && (
            <Input
              name="taxNumber" placeholder="الرقم الضريبي (مطلوب للعميل التجاري)"
              value={form.taxNumber} onChange={handleChange}
              required
            />
          )}

          <div className="grid grid-cols-2 gap-2">
            <Input
              name="mobile" placeholder="الجوال"
              value={form.mobile} onChange={handleChange}
            />
            <Input
              name="phone" placeholder="الهاتف"
              value={form.phone} onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Input
              name="email" placeholder="البريد الإلكتروني"
              value={form.email} onChange={handleChange}
            />
            <Input
              name="address" placeholder="العنوان المختصر"
              value={form.address} onChange={handleChange}
            />
          </div>

          {/* العنوان الوطني */}
          <fieldset className="border p-4 rounded bg-gray-50">
            <legend className="font-bold text-sm px-2 text-blue-600">العنوان الوطني</legend>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Input
                name="buildingNumber" placeholder="رقم المبنى"
                value={form.buildingNumber} onChange={handleChange}
              />
              <Input
                name="additionalNumber" placeholder="الرقم الفرعي"
                value={form.additionalNumber} onChange={handleChange}
              />
              <Input
                name="unitNumber" placeholder="رقم الوحدة"
                value={form.unitNumber} onChange={handleChange}
              />
              <Input
                name="streetName" placeholder="اسم الشارع"
                value={form.streetName} onChange={handleChange}
              />
              <Input
                name="district" placeholder="الحي"
                value={form.district} onChange={handleChange}
              />
              <Input
                name="city" placeholder="المدينة"
                value={form.city} onChange={handleChange}
              />
              <Input
                name="country" placeholder="الدولة"
                value={form.country} onChange={handleChange}
              />
              <Input
                name="postalCode" placeholder="الرمز البريدي"
                value={form.postalCode} onChange={handleChange}
              />
              <Input
                name="shortAddress" placeholder="العنوان المختصر (Short Address)"
                value={form.shortAddress} onChange={handleChange}
              />
            </div>
          </fieldset>

          <Input
            name="notes" placeholder="ملاحظات"
            value={form.notes} onChange={handleChange}
          />

          <div className="flex items-center gap-2">
            <Switch
              id="isActive-switch"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
            />
            <label htmlFor="isActive-switch" className="cursor-pointer font-medium">
              نشط
            </label>
          </div>

          <div className="flex gap-2 mt-4">
            <Button type="submit">💾 حفظ</Button>
            <Button variant="outline" type="button" onClick={() => onCancel ? onCancel() : navigate('/customers')}>
              {isModal ? 'إلغاء' : '↩️ رجوع'}
            </Button>
          </div>
        </form>
      </Content>
    </Container>
  );
}
